import { FC, useEffect, useState, useRef, useCallback } from 'react';
import '@ant-design/v5-patch-for-react-19';
import { Link } from 'react-router-dom';
import { Table, Spin, Breadcrumb, Typography, Divider, Input, Select, Flex, message } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { fetchFerriteBeadProducts, searchProducts } from '../api/products';
import { FerriteBeadProduct, FerriteBeadProductFetchParams, Pagination } from '../types/product';
import LeftBar from './common/LeftBar';
import { useTranslation } from 'react-i18next';
import { HomeOutlined } from '@ant-design/icons';
import { isFerriteBeadProduct } from '../types/product'; // 导入类型守卫
import { throttle as lodashThrottle } from 'lodash-es';

const { Title, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;

const ProductListFerriteBead: FC = () => {
  const { t } = useTranslation(); 
  // 修改为 FerriteBeadProduct 类型
  const [allData, setAllData] = useState<FerriteBeadProduct[]>([]); 
  const [filteredData, setFilteredData] = useState<FerriteBeadProduct[]>([]); 
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination>({
    current: 1,
    pageSize: 25, 
    total: 0
  });

  // 搜索状态
  const [searchValue, setSearchValue] = useState('');
  const [impedance100MHzFilter, setImpedance100MHzFilter] = useState<string | undefined>();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768); 

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 修改为 FerriteBeadProduct 类型
  const columns: ColumnsType<FerriteBeadProduct> = [
    {
      title: 'Part Number',
      dataIndex: 'part_number',
      key: 'part_number',
      render: (text: string, record: FerriteBeadProduct) => (
        <Link to={`/products-ferrite/${record.part_number}`}>{text}</Link>
      ),
      fixed: isMobile ? undefined : 'left'
    },
    {
      title: 'Previous Part Number',
      dataIndex: 'product_code',
      key: 'product_code',
      fixed: isMobile ? undefined : 'left'
    },
    {
      title: 'Size(EIA)',
      dataIndex: ['specs', 'external_dimensions'],
      key: 'specs.external_dimensions',
      render: (_, record) => {
        if (isFerriteBeadProduct(record)) {
          return record.specs.external_dimensions;
        }
        return null;
      }
    },
    {
      title: 'Size(LxW)',
      dataIndex: ['specs', 'size'],
      key: 'specs.size',
      render: (_, record) => {
        if (isFerriteBeadProduct(record)) {
          return record.specs.size;
        }
        return null;
      }
    },
    {
      title: 'Temp.',
      dataIndex: ['specs', 'temperature_range'],
      key: 'specs.temperature_range',
      render: (_, record) => {
        if (isFerriteBeadProduct(record)) {
          return record.specs.temperature_range;
        }
        return null;
      }
    },
    {
      title: 'Impedance(100MHz)',
      dataIndex: ['specs', 'impedance_100mhz'],
      key: 'specs.impedance_100mhz',
      render: (_, record) => {
        if (isFerriteBeadProduct(record)) {
          return record.specs.impedance_100mhz;
        }
        return null;
      }
    },
    {
      title: 'Impedance(1GHz)',
      dataIndex: ['specs', 'impedance_1ghz'],
      key: 'specs.impedance_1ghz',
      render: (_, record) => {
        if (isFerriteBeadProduct(record)) {
          return record.specs.impedance_1ghz;
        }
        return null;
      }
    },
    {
      title: 'Thickness',
      dataIndex: ['specs', 'thickness'],
      key: 'specs.thickness',
      render: (_, record) => {
        if (isFerriteBeadProduct(record)) {
          return record.specs.thickness;
        }
        return null;
      }
    },
    {
      title: 'I (at 85℃)(max)',
      dataIndex: ['specs', 'rated_current_85'],
      key: 'specs.rated_current_85',
      render: (_, record) => {
        if (isFerriteBeadProduct(record)) {
          return record.specs.rated_current_85;
        }
        return null;
      }
    },
    {
      title: 'Rdc(max)',
      dataIndex: ['specs', 'dc_resistance_max'],
      key: 'specs.dc_resistance_max',
      render: (_, record) => {
        if (isFerriteBeadProduct(record)) {
          return record.specs.dc_resistance_max;
        }
        return null;
      }
    },
    {
      title: 'Z Tole.',
      dataIndex: ['specs', 'impedance_tolerance'],
      key: 'specs.impedance_tolerance',
      render: (_, record) => {
        if (isFerriteBeadProduct(record)) {
          return record.specs.impedance_tolerance;
        }
        return null;
      }
    },
    {
      title: 'Qualification',
      dataIndex: ['specs', 'qualification'],
      key: 'specs.qualification',
      render: (_, record) => {
        if (isFerriteBeadProduct(record)) {
          return record.specs.qualification;
        }
        return null;
      }
    },
    {
      title: 'COO',
      dataIndex: ['specs', 'COO'],
      key: 'specs.COO',
      render: (_, record) => {
        if (isFerriteBeadProduct(record)) {
          return record.specs.COO;
        }
        return null;
      }
    },
    {
      title: 'SPQ',
      dataIndex: ['specs', 'spq'],
      key: 'specs.spq',
      render: (_, record) => {
        if (isFerriteBeadProduct(record)) {
          return record.specs.spq;
        }
        return null;
      }
    },
   {
      title: 'Brand',
      key: 'brand',
      dataIndex: 'brand',
      render: () => 'TAIYO YUDEN'
    }
  ];

  const [fixedHeaderVisible, setFixedHeaderVisible] = useState(false);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);
  const [tableWidth, setTableWidth] = useState('100%');
  const tableRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  // 声明 scrollLeft 状态
  const [scrollLeft, setScrollLeft] = useState(0);

  const tableContainerRef = useRef<HTMLDivElement>(null);

  // 处理表格内容滚动事件
  const handleTableContentScroll = useCallback((e: Event) => {
    const newScrollLeft = (e.target as HTMLElement).scrollLeft;
    setScrollLeft(newScrollLeft);
    const fixedHeader = document.querySelector('.fixed-header') as HTMLElement;
    if (fixedHeader) {
      fixedHeader.scrollLeft = newScrollLeft;
    }
  }, []);

  useEffect(() => {
    const tableElement = tableRef.current;
    if (tableElement) {
      const tableContent = tableElement.querySelector('.ant-table-content') as HTMLElement;
      if (tableContent) {
        tableContent.addEventListener('scroll', handleTableContentScroll);
      }
    }

    return () => {
      if (tableRef.current) {
        const tableContent = tableRef.current.querySelector('.ant-table-content') as HTMLElement;
        if (tableContent) {
          tableContent.removeEventListener('scroll', handleTableContentScroll);
        }
      }
    };
  }, [handleTableContentScroll]);

  useEffect(() => {
    const handleScroll = () => {
      if (!headerRef.current) return;
      const headerRect = headerRef.current.getBoundingClientRect();
      setFixedHeaderVisible(headerRect.top < 0);
    };

    const handleResize = () => {
      if (tableRef.current) {
        setTableWidth(`${tableRef.current.offsetWidth}px`);
      }
    };

    const throttledScroll = lodashThrottle(handleScroll, 50);
    window.addEventListener('scroll', throttledScroll);
    window.addEventListener('resize', handleResize);

    // 初始化宽度
    handleResize();

    return () => {
      window.removeEventListener('scroll', throttledScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 获取列宽
  useEffect(() => {
    if (fixedHeaderVisible && headerRef.current) {
      const ths = headerRef.current.querySelectorAll('th');
      const widths = Array.from(ths).map(th => th.offsetWidth);
      setColumnWidths(widths);
    }
  }, [fixedHeaderVisible]);

  // 生成带宽度的列配置
  const fixedColumns = columns.map((col, index) => ({
    ...col,
    width: columnWidths[index] || 0,
    onHeaderCell: () => ({ 
      style: { 
        width: columnWidths[index] ? `${columnWidths[index]}px` : 'auto',
        whiteSpace: 'nowrap'
      }
    })
  }));

  // 加载数据
  const loadData = async (params: FerriteBeadProductFetchParams) => {
    setLoading(true);
    try {
      // 如果有搜索值，使用 searchProducts 函数
      if (searchValue) {
        // 使用 search 参数进行模糊查找
        const searchResponse = await searchProducts({
          part_number: searchValue,
          product_type: 'FerriteBead'
        });
        
        if (searchResponse && searchResponse.results && searchResponse.results.length > 0) {
          
          // 过滤出 FerriteBead 类型的产品
          const ferriteBeadProducts = searchResponse.results.filter(
            (product): product is FerriteBeadProduct => product.product_type === 'FerriteBead'
          );
          
          if (ferriteBeadProducts.length > 0) {
            setAllData(ferriteBeadProducts);
            setFilteredData(ferriteBeadProducts); 
            setPagination({
              ...pagination,
              total: ferriteBeadProducts.length
            });
            setLoading(false);
            return;
          }
        }
        
        // 如果没有找到结果，设置空数组
        setAllData([]);
        setFilteredData([]); 
        setPagination({
          ...pagination,
          total: 0
        });
        setLoading(false);
        return;
      }
      
      // 如果没有搜索值，使用原来的 fetchFerriteBeadProducts 函数
      const response = await fetchFerriteBeadProducts(params);
      
      if (response && response.results) {
        const ferriteBeadProducts = response.results.filter(isFerriteBeadProduct);
        setAllData(ferriteBeadProducts);
        setFilteredData(ferriteBeadProducts); 
        setPagination({
          ...pagination,
          total: response.count
        });
      } else {
        console.error('API返回数据格式不正确:', response);
        message.error('数据加载失败，返回格式不正确');
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    const result = allData.filter(product => {
      // 搜索商品型号和物料编码
      const searchMatch = searchValue === '' || 
        product.part_number.toLowerCase().includes(searchValue.toLowerCase()) ||
        product.product_code.toLowerCase().includes(searchValue.toLowerCase());

      // 100MHz 阻抗筛选
      const impedance100MHzMatch = impedance100MHzFilter == null || 
        product.specs.impedance_100mhz === impedance100MHzFilter;

      return searchMatch && impedance100MHzMatch;
    });

    setFilteredData(result);
    setPagination({
      ...pagination,
      total: result.length
    });
  };

  useEffect(() => {
    loadData({
      ...pagination,
      search: '',
      impedance_100mhz: null,
      size: null,
      rated_current_85: null,
    });
  }, []);

  useEffect(() => {
    filterData();
  }, [searchValue, impedance100MHzFilter]);

  const handleTableChange = (
    newPagination: TablePaginationConfig
  ) => {
    setPagination({
      current: newPagination.current || 1,
      pageSize: newPagination.pageSize || 25,
      total: pagination.total
    });
  };

  const getAllValues = (key: keyof FerriteBeadProduct['specs']) => {
    let values = allData.map(item => item.specs[key]).filter(value => value != null && value !== 'nan');
  
    if (key === 'impedance_100mhz' || key === 'size' || key === 'thickness' || key === 'rated_current_85') {
      values = values.sort((a, b) => {
        const aValue = a !== null && a !== undefined ? a.toString() : '';
        const bValue = b !== null && b !== undefined ? b.toString() : '';
  
        return aValue.localeCompare(bValue);
      });
      return Array.from(new Set(values));
    } else {
      values = values.filter(value => value !== null && value !== undefined && value !== 'nan');
    }
    return Array.from(new Set(values));
  };

  // 在状态管理部分添加滚动位置状态
  const [scrollY, setScrollY] = useState(0);
  const [showScrollBar, setShowScrollBar] = useState(false);
  // 添加滚动监听
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      if (loading) return;
      // 滚动条隐藏
      if (tableContainerRef.current) {
        const containerRect = tableContainerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const scrollThreshold = 80; // 距离底部50px时开始隐藏
  
        // 当表格底部接近视口底部时隐藏滚动条
        setShowScrollBar(containerRect.bottom > viewportHeight + scrollThreshold);
      }
    };
    const throttledScroll = lodashThrottle(handleScroll, 100);
    window.addEventListener('scroll', throttledScroll);
    return () => window.removeEventListener('scroll', throttledScroll);
  }, []);

  //模擬滾動條
  const scrollBarRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startScrollLeft, setStartScrollLeft] = useState(0);

  // 初始化滚动条
  useEffect(() => {
    const table = tableRef.current?.querySelector('.ant-table-content');
    if (!table || !scrollBarRef.current) return;

    // 计算滚动条尺寸
    const updateScrollBar = () => {
      const containerWidth = table.clientWidth;
      const contentWidth = table.scrollWidth;
      const ratio = containerWidth / contentWidth;
      if (thumbRef.current) {
        thumbRef.current.style.width = `${ratio * 100}%`;
      }
    };

    // 添加表格内容变化监听
    const resizeObserver = new ResizeObserver(updateScrollBar);
    resizeObserver.observe(table);

    // 添加数据加载完成后的延迟更新
    const initTimer = setTimeout(updateScrollBar, 500);
    
    // 监听窗口尺寸变化
    window.addEventListener('resize', updateScrollBar);

    // 同步表格滚动
    const syncScroll = () => {
      if (!thumbRef.current) return;
      const scrollLeft = table.scrollLeft;
      const maxScroll = table.scrollWidth - table.clientWidth;
      const thumbMax = scrollBarRef.current!.clientWidth - thumbRef.current.offsetWidth;
      
      thumbRef.current.style.left = `${(scrollLeft / maxScroll) * thumbMax}px`;
    };

    table.addEventListener('scroll', syncScroll);
    window.addEventListener('resize', updateScrollBar);
    updateScrollBar();

    return () => {
      clearTimeout(initTimer);
      resizeObserver.disconnect();
      table.removeEventListener('scroll', syncScroll);
      window.removeEventListener('resize', updateScrollBar);
    };
  }, [filteredData]);;

  // 鼠标事件处理
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setStartX(e.clientX);
    setStartScrollLeft(thumbRef.current!.offsetLeft);
  };
  const handleScrollBarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!scrollBarRef.current || !thumbRef.current) return;
    
    const rect = scrollBarRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const maxThumbScroll = scrollBarRef.current.offsetWidth - thumbRef.current.offsetWidth;
    
    // 计算新的滑块位置
    const newLeft = Math.min(Math.max(0, clickX - thumbRef.current.offsetWidth / 2), maxThumbScroll);
    thumbRef.current.style.left = `${newLeft}px`;
    
    // 同步表格滚动
    const table = tableRef.current?.querySelector<HTMLElement>('.ant-table-content');
    if (table) {
      const maxTableScroll = table.scrollWidth - table.clientWidth;
      table.scrollLeft = (newLeft / maxThumbScroll) * maxTableScroll;
    }
  };
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !scrollBarRef.current || !thumbRef.current) return;
    
    e.stopPropagation();
    e.preventDefault();
    const deltaX = e.clientX - startX;
    const maxThumbScroll = scrollBarRef.current.offsetWidth - thumbRef.current.offsetWidth;
    const newLeft = Math.min(Math.max(0, startScrollLeft + deltaX), maxThumbScroll);
    
    // 更新滑块位置
    thumbRef.current.style.left = `${newLeft}px`;
    
    // 同步表格滚动
    const table = tableRef.current?.querySelector<HTMLElement>('.ant-table-content');
    if (table) {
      const maxTableScroll = table.scrollWidth - table.clientWidth;
      table.scrollLeft = (newLeft / maxThumbScroll) * maxTableScroll;
    }
  };

  const handleMouseUp = (e?: MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setIsDragging(false);
  };

  // 添加全局事件监听
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, startX, startScrollLeft]);

  return (
    <div style={{paddingBottom: '80px'}}>
      <div className='' style={{background: '#f5f5f5'}}>
        <div className='container'>
          <Breadcrumb
            className="breadcrumbs"
            separator=">"
            items={[
              {
                title: (
                  <>
                    <HomeOutlined />
                    <span>{t("home")}</span>
                  </>
                ),
                href: '/',
              },
              {
                title: t("page.product.title"),
                href: '/products',
              },
              {
                title: t("page.product.categories.ferrite.title"),
              },
            ]}
          />
        </div>
      </div>
      <div className='container'>
        <Flex gap={20} align="flex-start">
          {/* 左侧固定侧边栏 */}
          {!isMobile && (
          <div style={{
            width: 200,
            height: '100%',
            position: 'sticky',
            overflowY: 'auto',
            background: '#ffffff',
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2px 8px',
            top: Math.max(218 - scrollY, 0), // 初始偏移120px，随滚动逐渐置顶
            transition: 'top 0.3s ease',
          }}>
            <LeftBar />
          </div>
          )}
          {/* 右侧内容区域 */}
          <div style={{ 
            flex: 1,
            width: isMobile ? '100%' : 'calc(100% - 220px)',
            minWidth: 0 
          }}>
            <div className='mt-30 mb-30'>
              <Title className='text-center text-primary' level={1}>{t("page.product.categories.ferrite.title")}</Title>
              <Divider />
              <Paragraph className='text-center text-info font-size1'>{t("page.product.categories.ferrite.description")}</Paragraph>
            </div>

            {/* 搜索条件 */}
            <Flex style={{ marginBottom: 16 }}>
              <Search
                placeholder="搜索商品型号和物料编码"
                onSearch={value => setSearchValue(value)}
                style={{ width: 400,marginRight: 16 }}
              />
              <Select
                placeholder="选择100MHz 阻抗"
                onChange={value => setImpedance100MHzFilter(value)}
                allowClear
                style={{ width: 200, marginRight: 16 }}
              >
                {getAllValues('impedance_100mhz').map((value, index) => (
                  <Option key={`impedance_100mhz_${index}`} value={value}>{value}</Option>
                ))}
              </Select>
              
            </Flex>

            <div 
              ref={tableContainerRef}
              className='table-center'
              style={{ 
                position: 'relative',
                overflowX: 'auto',
                paddingTop: fixedHeaderVisible ? '55px' : '0' 
              }}
            >
              {/* 固定表头 */}
              {fixedHeaderVisible && (
                <div
                  style={{
                    width: tableWidth,
                    overflowX: 'hidden',
                    position: 'fixed',
                    height: '55px',
                    top: 0,
                    zIndex: 1000,
                  }}
                >
                    <div
                    className="fixed-header"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: -scrollLeft,
                        zIndex: 1000,
                        background: '#fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        overflow: 'hidden',
                        pointerEvents: 'none'
                    }}
                    >
                    <Table
                        columns={fixedColumns}
                        dataSource={[]}
                        showHeader={true}
                        pagination={false}
                        components={{
                        header: {
                            wrapper: props => <thead {...props} style={{ margin: 0 }}/>
                        },
                        body: {
                            wrapper: () => <tbody style={{ display: 'none' }}/>
                        }
                        }}
                        scroll={{ x: 'max-content' }}
                    />
                    </div>
                </div>
              )}

              <div  ref={tableRef}>
                
              <Table<FerriteBeadProduct>
                columns={columns}
                dataSource={filteredData}
                rowKey="part_number"
                components={{
                  header: {
                    wrapper: props => (
                      <thead 
                        {...props} 
                        ref={headerRef}
                        style={{ visibility: fixedHeaderVisible ? 'hidden' : 'visible' }}
                      />
                    )
                  }
                }}
                pagination={{
                  total: pagination.total,
                  showTotal: (total, range) => `显示第 ${range[0]} 到 ${range[1]} 项，共 ${total} 项`,
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  showSizeChanger: true,
                  position: ['bottomCenter'],
                  pageSizeOptions: ['25', '50', '100'], 
                  className: 'mt-30'
                }}
                loading={{
                  spinning: loading,
                  indicator: <Spin size="large" />
                }}
                onChange={handleTableChange}
                bordered
                scroll={{ x: 'max-content' }}
                style={{ marginTop: fixedHeaderVisible ? -40 : 0 }} 
              />
              </div>
            </div>
            {!isMobile && (
            <div>
              {/* 自定义滚动条 */}
              <div
                ref={scrollBarRef}
                onMouseDown={handleScrollBarClick}
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'fixed',
                  bottom: '3px',
                  width: `calc(100% - 260px)`,
                  height: '12px',
                  backgroundColor: '#eeeeee',
                  borderRadius: '6px',
                  zIndex: 999,
                  cursor: 'pointer',
                  display: showScrollBar ? 'block' : 'none', 
                  transition: 'opacity 0.3s ease' ,
                  maxWidth: '1140px'
                }}
              >
                <div
                  className="scroll-thumb"
                  ref={thumbRef}
                  onMouseDown={handleMouseDown}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    height: '10px',
                    backgroundColor: '#bfbfbf',
                    borderRadius: '6px',
                    top: '2px',
                    transition: 'width 0.2s',
                    userSelect: 'none'
                  }}
                />
              </div>
            </div>
            )}
          </div>
        </Flex>
      </div>
    </div>
  );
};

export default ProductListFerriteBead;